import React from "react";
import styles from "./Table.scss";
import { TableBody } from "./TableBody";
import { TableFooter } from "./TableFooter";
import { Lottie } from "~views/shared";
import { TableHeader } from "./TableHeader";
import loadingAnimationData from "~assets/lottie/loading.json";

export interface IColumn<T, K extends keyof T = keyof T> {
  title: string;
  accessor: K;
  render?: (value: T[K], row: T, rowIndex: number) => React.ReactNode;
  align?: "left" | "center" | "right";
  ellipsis?: boolean;
  hide?: boolean;
  width?: string | number;
}

type ITableProps<T, K extends keyof T> = SingleTableProps<T, K> | MutliTableProps<T, K>;

interface BaseTableProps<T, K extends keyof T> {
  isMultiple?: boolean;
  columns: IColumn<T, K>[];
  action?: (row: T, rowIndex: number) => React.ReactNode; // Displayed as last column
  extra?: (row: T) => React.ReactNode; // Displayed under row
  rowClassName?: (row: T) => string | undefined;
  total?: { title: string; value: React.ReactNode };
  loading?: boolean;
  onRowClick?: (row: T) => void;
  layoutFixed?: boolean;
}

interface SingleTableProps<T, K extends keyof T> extends BaseTableProps<T, K> {
  data: T[];
  isMultiple?: false;
}

interface MutliTableProps<T, K extends keyof T> extends BaseTableProps<T, K> {
  data: { title: string; key: string; className?: string; data: T[] }[];
  isMultiple: true;
}

export const Table = <T, K extends keyof T>({
  data,
  extra,
  action,
  total,
  columns,
  layoutFixed = true,
  loading,
  isMultiple,
  rowClassName,
  onRowClick,
}: ITableProps<T, K>) => {
  if (loading)
    return (
      <Lottie
        options={{
          animationData: loadingAnimationData,
        }}
        height={100}
        width={100}
      />
    );

  if (!data.length) return null;

  const displayedColumns = columns.filter((column) => column.hide !== true);
  const hasActionColumn = typeof action === "function";
  const tableStyles = [styles.table];

  if(layoutFixed){
    tableStyles.push(styles.layoutFixed);
  }

  return (
    <div className={styles.wrapper}>
      <table className={tableStyles.join(" ")}>
        <TableHeader columns={displayedColumns} hasAction={hasActionColumn} />
        {isMultiple ? (
          data.map((entry) => (
            <TableBody
              key={entry.key}
              title={entry.title}
              data={entry.data}
              className={entry.className}
              columns={displayedColumns}
              rowClassName={rowClassName}
              onRowClick={onRowClick}
              extra={extra}
              hasFooter={Boolean(total)}
              hasAction={hasActionColumn}
              action={action}
            />
          ))
        ) : (
          <TableBody
            data={data}
            columns={displayedColumns}
            onRowClick={onRowClick}
            extra={extra}
            hasFooter={Boolean(total)}
            hasAction={hasActionColumn}
            action={action}
          />
        )}
        <TableFooter total={total} columnCount={displayedColumns.length} hasAction={hasActionColumn} />
      </table>
    </div>
  );
};
